import PropTypes from 'prop-types';
import {
  Divider, List, ListItem, makeStyles,
} from '@material-ui/core';
import _ from 'lodash';
import {
  Fragment, memo, useCallback, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import NumberFormat from 'react-number-format';
import { useAnalytics } from 'use-analytics';
import tw from 'twin.macro';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import AddButton from '../../../../AddButton';
import Button from '../../../../Button';
import { addToTrip, removeFromTrip } from '../../../../../store/slices/trip';
import RateDetail from './RateDetail';

const PricingDetail = ({ tripId, itinerary, doShowPrice }) => {
  const classes = makeStyles(() => ({ root: tw`justify-between` }))();

  const [currentRateDetail, setCurrentRateDetail] = useState();
  const { track } = useAnalytics();
  const dispatch = useDispatch();
  const { tripConfirmation } = useSelector((store) => store.trip);
  const selectedTrip = useSelector((store) => store.trip
    .selectedTrips[tripConfirmation && tripConfirmation.tripId ? tripConfirmation.tripId : tripId]);
  const doCheckoutOnSelect = false;

  const checkIsSelected = useCallback((item) => {
    if (selectedTrip && selectedTrip.customInventoryItinerary) {
      return selectedTrip.customInventoryItinerary.selectedProductId === item.id;
    }
    return false;
  }, [selectedTrip]);

  const handleRemoveFromTrip = useCallback(() => {
    dispatch(removeFromTrip({ tripId, key: 'customInventoryItinerary' }));
  }, [dispatch, tripId]);

  const handleViewRatesClick = (product) => {
    const id = currentRateDetail && currentRateDetail === product.id ? null : product.id;
    setCurrentRateDetail(id);
  };

  const handleSelectProductClick = (product) => {
    if (tripId) {
      dispatch(addToTrip({
        tripId,
        trip: {
          customInventoryItinerary: {
            ...itinerary,
            selectedProductId: product.id,
          },
        },
      }));
    }
    track('PricingDetail.handleSelectProductClick', {
      product: JSON.stringify(product),
    });
  };

  return (
    <List>
      {(tripConfirmation && tripConfirmation.tripId && !doShowPrice
        ? _.filter(itinerary.products, (p) => checkIsSelected(p))
        : itinerary.products).map((item, index, array) => (
          <Fragment key={item.id}>
            <ListItem
              classes={classes}
              className={checkIsSelected(item)
                ? 'border-2 border-theme border-opacity-25 bg-theme bg-opacity-25' : 'justify-between'}
            >
              <div className="grid grid-cols-9 w-full">
                <div className={clsx({ 'sm:col-span-7 col-span-6': doShowPrice, 'col-span-9': !doShowPrice })}>
                  {item.name}
                  {item.shortDescription && item.shortDescription.length ? (
                    <div className="text-xs text-gray-600 mt-1">
                      {item.shortDescription}
                    </div>
                  ) : <></>}
                </div>
                <div className="sm:col-span-2 col-span-3 text-right mb-1">
                  <div className="text-right inline-block whitespace-no-wrap">
                    {doShowPrice && (
                    <div className="font-bold">
                      <div className="font-bold text-center">
                        <NumberFormat
                          value={item.pricing.markupPrice}
                          displayType="text"
                          thousandSeparator
                          prefix="Total $"
                          fixedDecimalScale="true"
                          decimalScale="2"
                        />
                      </div>
                    </div>
                    )}
                    {tripId && !doCheckoutOnSelect && (
                    <AddButton
                      isSelected={checkIsSelected(item)}
                      onAdd={() => handleSelectProductClick(item)}
                      onRemove={handleRemoveFromTrip}
                      addLabel="Add to Trip"
                      removeLabel="Remove"
                      buttonSize={selectedTrip
                        ? Button.buttonSizes.XS : undefined}
                    />
                    )}
                    {tripId && doCheckoutOnSelect && (
                    <Button
                      onClick={() => {}}
                      size={Button.buttonSizes.SM}
                    >
                      Select
                    </Button>
                    )}

                    {item.rateDetail && item.rateDetail.length ? (
                      <div className="col-span-9 text-left flex justify-between mt-1">
                        <div
                          className="text-base text-theme cursor-pointer z-10 flex items-center"
                          onClick={() => handleViewRatesClick(item)}
                          role="button"
                          onKeyDown={() => handleViewRatesClick(item)}
                          tabIndex="-1"
                        >
                          {currentRateDetail === item.id && (
                          <>
                            Hide rate detail
                            <FaChevronUp className="pl-1" />
                          </>
                          )}
                          {currentRateDetail !== item.id && (
                          <>
                            View rate detail
                            <FaChevronDown className="pl-1" />
                          </>
                          )}
                        </div>
                      </div>
                    ) : <></>}

                  </div>
                </div>
                {currentRateDetail === item.id && <div className="col-span-9"><RateDetail product={item} /></div>}

              </div>
            </ListItem>
            {index !== array.length - 1 && <Divider />}
          </Fragment>
      ))}
    </List>
  );
};

PricingDetail.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  doShowPrice: PropTypes.bool,
  tripId: PropTypes.string,
};

PricingDetail.defaultProps = {
  doShowPrice: true,
  tripId: undefined,
};

export default memo(PricingDetail);
